<script setup>
import { ref, onMounted, computed, nextTick } from "vue";
import { listTaxes, addTax, deleteTax, updateTax } from "../db/taxes.js";
import ChatAssistant from "../components/ChatAssistant.vue";
import ConfirmModal from "../components/ConfirmModal.vue";
import AlertModal from "../components/AlertModal.vue";
import gsap from "gsap";

const taxes = ref([]);
const loading = ref(false);
const showModal = ref(false);
const newTax = ref({ name: "", rate: 0 });

// Filters State
const filters = ref({
  search: "",
  minRate: null,
  maxRate: null,
  sortBy: "name_asc"
});

// Search Suggestions State
const showTaxSuggestions = ref(false);
const hoverTaxIndex = ref(-1);

const taxSuggestions = computed(() => {
  if (!filters.value.search || !filters.value.search.trim()) {
    return taxes.value.slice(0, 8);
  }
  const q = filters.value.search.toLowerCase();
  return taxes.value.filter(t =>
    (t.name || "").toLowerCase().includes(q)
  ).slice(0, 8);
});

function onTaxSearchFocus() {
  showTaxSuggestions.value = true;
}

function onTaxSearchBlur() {
  setTimeout(() => {
    showTaxSuggestions.value = false;
  }, 200);
}

function onTaxSearchKeydown(e) {
  if (!showTaxSuggestions.value || !taxSuggestions.value.length) return;

  if (e.key === "ArrowDown") {
    e.preventDefault();
    hoverTaxIndex.value = (hoverTaxIndex.value + 1) % taxSuggestions.value.length;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    hoverTaxIndex.value = (hoverTaxIndex.value - 1 + taxSuggestions.value.length) % taxSuggestions.value.length;
  } else if (e.key === "Enter") {
    e.preventDefault();
    const tax = taxSuggestions.value[hoverTaxIndex.value >= 0 ? hoverTaxIndex.value : 0];
    if (tax) selectTaxSuggestion(tax);
  } else if (e.key === "Escape") {
    showTaxSuggestions.value = false;
  }
}

function selectTaxSuggestion(tax) {
  filters.value.search = String(tax.name).trim();
  showTaxSuggestions.value = false;
}

// Computed Filtered Taxes
const filteredTaxes = computed(() => {
  let result = [...taxes.value];

  // Search Filter
  if (filters.value.search) {
    const term = filters.value.search.toLowerCase();
    result = result.filter(t => t.name.toLowerCase().includes(term));
  }

  // Rate Range Filter
  if (filters.value.minRate !== null && filters.value.minRate !== "") {
    result = result.filter(t => t.rate >= Number(filters.value.minRate));
  }
  if (filters.value.maxRate !== null && filters.value.maxRate !== "") {
    result = result.filter(t => t.rate <= Number(filters.value.maxRate));
  }

  // Sorting
  result.sort((a, b) => {
    switch (filters.value.sortBy) {
      case 'name_asc': return a.name.localeCompare(b.name);
      case 'name_desc': return b.name.localeCompare(a.name);
      case 'rate_asc': return a.rate - b.rate;
      case 'rate_desc': return b.rate - a.rate;
      default: return 0;
    }
  });

  return result;
});

// State for ConfirmModal
const confirmState = ref({
  show: false,
  title: "",
  message: "",
  type: "warning",
  confirmText: "Confirmar",
  cancelText: "Cancelar",
  showCancel: true,
  onConfirm: () => { },
});

// State for AlertModal
const alertState = ref({
  show: false,
  title: "",
  message: "",
  type: "error",
  buttonText: "Entendido",
});

function showAlert(type, title, message) {
  alertState.value = {
    show: true,
    title,
    message,
    type,
    buttonText: "Entendido",
  };
}

onMounted(async () => {
  await loadTaxes();

  // GSAP Animations - wait for DOM
  await nextTick();
  await new Promise(resolve => setTimeout(resolve, 100));

  // Ensure elements are visible
  gsap.set(".page-header, .filters-bar, .card", { opacity: 1 });

  // Animate header
  gsap.from(".page-header", {
    duration: 0.7,
    y: -20,
    opacity: 0,
    ease: "power2.out"
  });

  // Animate filters
  gsap.from(".filters-bar", {
    duration: 0.7,
    y: 20,
    opacity: 0,
    delay: 0.1,
    ease: "power2.out"
  });

  // Animate cards if they exist
  const cards = document.querySelectorAll('.card');
  if (cards.length > 0) {
    gsap.from(cards, {
      duration: 0.6,
      y: 30,
      opacity: 0,
      scale: 0.95,
      stagger: 0.05,
      delay: 0.3,
      ease: "back.out(1.2)"
    });
  }
});

async function loadTaxes() {
  loading.value = true;
  try {
    taxes.value = await listTaxes();
  } catch (e) {
    console.error(e);
    showAlert("danger", "Error", "No se pudieron cargar los impuestos: " + (e.message || e));
  } finally {
    loading.value = false;
  }
}

async function saveTax() {
  if (!newTax.value.name || newTax.value.rate < 0) {
    showAlert("warning", "Datos inválidos", "El nombre es requerido y la tasa debe ser positiva.");
    return;
  }
  try {
    if (newTax.value.id_taxes) {
      await updateTax(newTax.value.id_taxes, { name: newTax.value.name, rate: newTax.value.rate });
      showAlert("success", "Éxito", "Impuesto actualizado correctamente.");
    } else {
      await addTax({ ...newTax.value });
      showAlert("success", "Éxito", "Impuesto agregado correctamente.");
    }
    await loadTaxes();
    showModal.value = false;
    newTax.value = { name: "", rate: 0 };
  } catch (e) {
    console.error(e);
    showAlert("danger", "Error", "No se pudo guardar el impuesto: " + (e.message || e));
  }
}

function editTax(tax) {
  newTax.value = { ...tax };
  showModal.value = true;
}

function confirmDelete(id) {
  confirmState.value = {
    show: true,
    title: "¿Eliminar impuesto?",
    message: "Esta acción no se puede deshacer.",
    type: "warning",
    confirmText: "Sí, eliminar",
    cancelText: "Cancelar",
    showCancel: true,
    onConfirm: async () => {
      confirmState.value.show = false;
      try {
        await deleteTax(id);
        await loadTaxes();
        showAlert("success", "Eliminado", "El impuesto ha sido eliminado.");
      } catch (e) {
        console.error(e);
        showAlert("danger", "Error", "No se pudo eliminar el impuesto: " + (e.message || e));
      }
    },
  };
}
</script>

<template>
  <main class="page-wrap">
    <header class="page-header">
      <div class="header-left">
        <h1 class="title">Impuestos</h1>
        <p class="subtitle">Gestiona las tasas impositivas</p>
      </div>
      <button class="btn-primary" @click="() => { newTax = { name: '', rate: 0 }; showModal = true; }">
        <span class="material-symbols-rounded">add</span>
        Nuevo Impuesto
      </button>
    </header>

    <section v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>Cargando impuestos...</p>
    </section>

    <!-- Filters Section -->
    <section v-else class="filters-container">
      <div class="filters-bar">
        <div class="search-box nav-search">
          <span class="material-symbols-rounded search-icon">search</span>
          <input v-model="filters.search" type="text" placeholder="Buscar impuesto..."
            class="filter-input search nav-input" @focus="onTaxSearchFocus" @blur="onTaxSearchBlur"
            @keydown="onTaxSearchKeydown" />
          <button v-if="filters.search" class="clear-btn" @click="filters.search = ''">
            <span class="material-symbols-rounded">close</span>
          </button>

          <div v-if="showTaxSuggestions && taxSuggestions.length > 0" class="nav-suggestions">
            <div v-for="(tax, index) in taxSuggestions" :key="tax.id_taxes" class="suggestion-item"
              :class="{ active: index === hoverTaxIndex }" @mouseenter="hoverTaxIndex = index"
              @mousedown.prevent="selectTaxSuggestion(tax)">
              <div class="suggestion-thumb placeholder-thumb">
                <span class="material-symbols-rounded">percent</span>
              </div>
              <div class="suggestion-info">
                <div class="suggestion-name">{{ tax.name }}</div>
                <div class="suggestion-meta">{{ tax.rate }}%</div>
              </div>
            </div>
          </div>
        </div>

        <div class="filter-group">
          <div class="range-inputs">
            <input v-model.number="filters.minRate" type="number" placeholder="Min %" class="filter-input small" />
            <span class="separator">-</span>
            <input v-model.number="filters.maxRate" type="number" placeholder="Max %" class="filter-input small" />
          </div>

          <select v-model="filters.sortBy" class="filter-select">
            <option value="name_asc">Nombre (A-Z)</option>
            <option value="name_desc">Nombre (Z-A)</option>
            <option value="rate_asc">Tasa (Menor a Mayor)</option>
            <option value="rate_desc">Tasa (Mayor a Menor)</option>
          </select>

          <button class="btn-icon reset-filters" title="Limpiar filtros"
            @click="filters = { search: '', minRate: null, maxRate: null, sortBy: 'name_asc' }">
            <span class="material-symbols-rounded">filter_alt_off</span>
          </button>
        </div>
      </div>

      <div class="grid">
        <div v-for="tax in filteredTaxes" :key="tax.id_taxes" class="card">
          <div class="card-icon">
            <span class="material-symbols-rounded">percent</span>
          </div>
          <div class="card-info">
            <h3>{{ tax.name }}</h3>
            <p class="rate">{{ tax.rate }}%</p>
          </div>
          <div class="card-actions">
            <button class="btn-icon edit" @click="editTax(tax)" title="Editar">
              <span class="material-symbols-rounded">edit</span>
            </button>
            <button class="btn-icon delete" @click="confirmDelete(tax.id_taxes)" title="Eliminar">
              <span class="material-symbols-rounded">delete</span>
            </button>
          </div>
        </div>
        <div v-if="filteredTaxes.length === 0" class="empty-state">
          <span class="material-symbols-rounded empty-icon">{{ taxes.length === 0 ? 'money_off' : 'search_off' }}</span>
          <p v-if="taxes.length === 0">No hay impuestos registrados.</p>
          <p v-else>No se encontraron resultados para los filtros aplicados.</p>
        </div>
      </div>
    </section>

    <!-- Modal Form (Add Tax) -->
    <Transition name="fade">
      <div v-if="showModal" class="modal-backdrop" @click.self="showModal = false">
        <div class="modal">
          <header class="modal-header">
            <h2>{{ newTax.id_taxes ? 'Editar Impuesto' : 'Nuevo Impuesto' }}</h2>
            <button class="close-btn" @click="showModal = false">
              <span class="material-symbols-rounded">close</span>
            </button>
          </header>
          <div class="modal-body">
            <div class="form-group">
              <label>Nombre</label>
              <input v-model="newTax.name" type="text" placeholder="Ej. IVA" class="input-field" autofocus />
            </div>
            <div class="form-group">
              <label>Tasa (%)</label>
              <select v-model.number="newTax.rate" class="input-field">
                <option v-for="n in 101" :key="n - 1" :value="n - 1">{{ n - 1 }}%</option>
              </select>
            </div>
          </div>
          <footer class="modal-footer">
            <button class="btn-secondary" @click="showModal = false">Cancelar</button>
            <button class="btn-primary" @click="saveTax">Guardar</button>
          </footer>
        </div>
      </div>
    </Transition>

    <!-- Confirm Modal (for deletions) -->
    <ConfirmModal :show="confirmState.show" :title="confirmState.title" :message="confirmState.message"
      :type="confirmState.type" :confirm-text="confirmState.confirmText" :cancel-text="confirmState.cancelText"
      :show-cancel="confirmState.showCancel" @confirm="confirmState.onConfirm" @cancel="confirmState.show = false" />

    <!-- Alert Modal (for errors/success) -->
    <AlertModal :show="alertState.show" :title="alertState.title" :message="alertState.message" :type="alertState.type"
      :button-text="alertState.buttonText" @close="alertState.show = false" />
  </main>
  <ChatAssistant />
</template>

<style scoped>
.page-wrap {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
}

/* Filters Bar */
.filters-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
  background: var(--surface);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  padding: 1.25rem;
  border-radius: 20px;
  border: 1px solid rgba(198, 146, 61, 0.2);
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(198, 146, 61, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: visible;
  z-index: 10;
}

.filters-bar::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(198, 146, 61, 0.1), transparent);
  transition: left 0.5s;
}

.filters-bar:hover::before {
  left: 100%;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 400px;
  z-index: 100;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
}

.filter-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.8rem;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text);
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s;
}

.filter-input:focus {
  border-color: #C6923D;
  box-shadow: 0 0 0 3px rgba(198, 146, 61, 0.1);
}

.filter-group {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.range-inputs {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-input.small {
  width: 80px;
  padding-left: 0.75rem;
  padding-right: 0.5rem;
}

.separator {
  color: var(--muted);
  font-weight: bold;
}

.filter-select {
  padding: 0.75rem 2rem 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text);
  font-size: 0.95rem;
  outline: none;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%236b7280%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 0.7rem top 50%;
  background-size: 0.65rem auto;
}

.reset-filters {
  color: var(--muted);
}

.reset-filters:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.title {
  font-size: 2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
}

.subtitle {
  color: var(--muted);
  margin-top: 0.5rem;
  font-size: 1rem;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.card {
  background: var(--surface);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(198, 146, 61, 0.2);
  border-radius: 24px;
  padding: 1.75rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  position: relative;
  box-shadow: 0 8px 32px rgba(198, 146, 61, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
}

.card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #C6923D, #DFC05E, #06b6d4);
  opacity: 0;
  transition: opacity 0.3s;
}

.card:hover::before {
  opacity: 1;
}

.card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 60px rgba(198, 146, 61, 0.25),
    0 8px 16px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  border-color: rgba(198, 146, 61, 0.4);
}

.card-icon {
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  color: white;
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  box-shadow: 0 8px 24px rgba(198, 146, 61, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.card:hover .card-icon {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 12px 32px rgba(198, 146, 61, 0.4);
}

.card-info {
  flex: 1;
}

.card-info h3 {
  font-weight: 700;
  margin: 0;
  color: var(--text);
  font-size: 1.1rem;
}

.rate {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--text);
  margin-top: 0.25rem;
  letter-spacing: -0.02em;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.6rem;
  border-radius: 12px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
}

.btn-icon:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--text);
}

.btn-icon.edit:hover {
  background: rgba(198, 146, 61, 0.1);
  color: #C6923D;
  transform: scale(1.1);
}

.btn-icon.delete:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  transform: scale(1.1);
}

.btn-primary {
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 50%, #06b6d4 100%);
  color: white;
  border: none;
  padding: 0.875rem 1.75rem;
  border-radius: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 24px rgba(198, 146, 61, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.btn-primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s;
}

.btn-primary:hover::before {
  left: 100%;
}

.btn-primary:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 12px 32px rgba(198, 146, 61, 0.4),
    0 4px 12px rgba(223, 192, 94, 0.2);
}

.btn-secondary {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text);
  padding: 0.75rem 1.5rem;
  border-radius: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: var(--bg);
}

/* Empty State */
.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: var(--muted);
  background: var(--surface);
  border-radius: 24px;
  border: 2px dashed var(--border);
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

/* Loading */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: var(--muted);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(198, 146, 61, 0.1);
  border-top-color: #C6923D;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Modal Form */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  backdrop-filter: blur(8px);
}

.modal {
  background: var(--surface);
  border-radius: 24px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border: 1px solid var(--border);
  overflow: hidden;
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--bg);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--muted);
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--text);
}

.modal-body {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text);
}

.input-field {
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text);
  font-size: 1rem;
  outline: none;
  transition: all 0.2s;
}

.input-field:focus {
  border-color: #C6923D;
  box-shadow: 0 0 0 4px rgba(198, 146, 61, 0.1);
  background: var(--surface);
}

select.input-field {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%236b7280%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 0.8rem auto;
  padding-right: 2.5rem;
  cursor: pointer;
}

.modal-footer {
  padding: 1.5rem 2rem;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  background: var(--bg);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Dark Mode Manual Toggle Support */
:global(:root[data-theme="dark"]) .card,
:global(:root[data-theme="dark"]) .modal,
:global(:root[data-theme="dark"]) .filters-bar {
  background: #1e293b;
  border-color: #334155;
}

:global(:root[data-theme="dark"]) .modal-header,
:global(:root[data-theme="dark"]) .modal-footer {
  background: #0f172a;
}

:global(:root[data-theme="dark"]) .input-field,
:global(:root[data-theme="dark"]) .filter-input,
:global(:root[data-theme="dark"]) .filter-select {
  background: #0f172a;
  border-color: #334155;
  color: #f1f5f9;
}

:global(:root[data-theme="dark"]) select.input-field {
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%2394a3b8%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
  color-scheme: dark;
}

:global(:root[data-theme="dark"]) select.input-field option {
  background-color: #0f172a;
  color: #f1f5f9;
}

:global(:root[data-theme="dark"]) .btn-secondary:hover {
  background: #334155;
}

:global(:root[data-theme="dark"]) .close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

:global(:root[data-theme="dark"]) .empty-state {
  background: #1e293b;
  border-color: #334155;
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-wrap {
    padding: 1.5rem;
  }

  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    width: 100%;
  }

  .filter-group {
    width: 100%;
    justify-content: space-between;
  }

  .range-inputs {
    flex: 1;
  }

  .filter-input.small {
    flex: 1;
  }

  .filter-select {
    flex: 1;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }

  .grid {
    grid-template-columns: 1fr;
  }

  .modal {
    max-width: 100%;
    margin: 1rem;
    border-radius: 20px;
  }

  .modal-header,
  .modal-footer {
    padding: 1.25rem 1.5rem;
  }

  .modal-body {
    padding: 1.5rem;
  }
}

/* Dropdown Search Styles */
.nav-search {
  position: relative;
}

.clear-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: var(--muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.clear-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--text);
}

.nav-suggestions {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: var(--surface);
  border-radius: 12px;
  border: 1px solid var(--border);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 9999;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid var(--border);
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover,
.suggestion-item.active {
  background: var(--bg);
}

.suggestion-thumb {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  background: var(--bg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  flex-shrink: 0;
}

.suggestion-thumb.placeholder-thumb {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.1), rgba(223, 192, 94, 0.1));
  color: #C6923D;
}

.suggestion-info {
  flex: 1;
  overflow: hidden;
}

.suggestion-name {
  font-weight: 600;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.suggestion-meta {
  font-size: 0.85rem;
  color: var(--muted);
}

/* Dark Mode Overrides for Suggestions */
:global(:root[data-theme="dark"]) .nav-suggestions {
  background-color: #1e293b !important;
  border-color: #334155;
}

:global(:root[data-theme="dark"]) .suggestion-item {
  border-color: #334155;
  background-color: #1e293b;
}

:global(:root[data-theme="dark"]) .suggestion-item:hover,
:global(:root[data-theme="dark"]) .suggestion-item.active {
  background-color: #334155 !important;
}
</style>

<style>
/* Global dark mode overrides for select options - placed outside scoped styles to ensure specificity */
:root[data-theme="dark"] select,
:root[data-theme="dark"] select option,
:root[data-theme="dark"] .input-field option {
  background-color: #1e293b !important;
  color: #ffffff !important;
  color-scheme: dark;
}

:root[data-theme="dark"] select:focus option {
  background-color: #1e293b !important;
  color: #ffffff !important;
}
</style>
